<template>
  <component
    :is="element"
    class="highlighter-title"
    :class="[
      classes,
      $blockClass(blockClass, 'highlighter-title'),
      highlighterSize,
      { 'highlighter-title--mounted': isMounted }
    ]"
  >
    <span class="highlighter-title__container">
      <span
        ref="highlights"
        class="highlighter-title__wrapper"
        v-html="highlightedCopy"
      />
    </span>
  </component>
</template>

<script>

/**
 * Vue: Highlighter title (highlighter-title)
 * -----------------------------------------------------------------------------
 * Custom Title with selectable highlighted copy.
 * - Rich text content will be stripped of <p> tags
 *
 * @param {String} blockClass - Block level class to apply to all elements.
 * @param {String} [classes] - Additional classes to apply to component.
 * @param {String} [customElement] - Optional override to set div element.
 * @param {String} [highlighterCopy] - The content with highlightable text.
 * Typically straight from Liquid's rich text setting.
 * @param {String} [highlighterColor] - The color of the highlight.
 * @param {String} [highlighterSize] - Optional override to 'h1' size
 *
 */
export default {
  name: 'HighlighterTitle',

  props: {
    animationDuration: {
      type: Number,
      default: 800,
    },
    blockClass: {
      type: String,
      default: '',
    },
    classes: {
      type: String,
      default: '',
    },
    customElement: {
      type: String,
      default: '',
    },
    highlighterCopy: {
      type: String,
      default: '',
    },
    highlighterColor: {
      type: String,
      default: '',
    },
    highlighterSize: {
      type: String,
      default: 'h1',
    },
  },

  data() {
    return {
      global: true,
      highlightClasses: `${this.highlighterSize} highlight highlight--${this.highlighterColor}`,
      isMounted: false,
    }
  },

  computed: {

    /**
     * Computed highlighted text HTML.
     * @returns {String}
     */
    highlightedCopy() {
      const highlighterCopy = this.highlighterCopy
      const strongIndexes = this.getIndexes(highlighterCopy, '<strong>')
      const strongIndexesClose = this.getIndexes(highlighterCopy, '</strong>')
      let finalText = ''

      strongIndexes.forEach((strongIndex, index) => {
        if (index === 0) {
          finalText = highlighterCopy.slice(0, strongIndex)
        }
        else {
          finalText = finalText + ' ' + highlighterCopy.slice(strongIndexesClose[index - 1] + 9, strongIndexes[index])
        }
        finalText = finalText + this.replaceStrongWithPre(strongIndex, strongIndexesClose[index])
        if (index + 1 === strongIndexes.length) {
          finalText = finalText + ' ' + highlighterCopy.slice(strongIndexesClose[index] + 9, highlighterCopy.length)
        }
      })

      if (strongIndexes.length === 0) {
        return this.$cleanRTE(highlighterCopy)
      }

      return this.$cleanRTE(finalText)
    },

    /**
     * Compute element type.
     * @returns {String}
     */
    element() {
      if (this.customElement) {
        return this.customElement
      }

      return 'div'
    },
  },

  mounted() {
    setTimeout(() => {
      this.isMounted = true
    }, 0)
  },

  methods: {

    getVars(index, totalHighlights) {
      const speed = this.animationDuration / totalHighlights
      const delay = index * (speed) + this.animationDuration
      return `--delay: ${delay}ms; --speed: ${speed}ms;`
    },

    getIndexes(string, searchItem) {
      const indexes = []
      let i = string.indexOf(searchItem)
      while (i !== -1) {
        indexes.push(i)
        i = string.indexOf(searchItem, i + searchItem.length)
      }
      return indexes
    },

    replaceStrongWithPre(strongIndex, strongIndexClose) {
      const highlightedText = this.highlighterCopy.slice(
        strongIndex + 8, strongIndexClose,
      )
      return highlightedText
        .trim()
        .split(' ')
        .map((word, index, arr) => {
          if (index !== 0) {
            word = ` ${word}`
          }
          return `<pre
            class="${this.highlightClasses}"
            style="${this.getVars(index, arr.length)}"
          >${word}</pre>`
        })
        .join('')
    },
  },
}
</script>
